import { FormEvent, useEffect, useRef, useState, useMemo, useCallback, useLayoutEffect, useContext } from "react";
import { Row, Col, Form, Button, ButtonGroup, Table, Pagination, Modal, Alert, Tabs, Tab } from "react-bootstrap";
import { PlusCircle, PencilFill, Trash, CollectionPlayFill, Check2Circle, Pencil, PlusCircleDotted, Plus, DashCircle, FiletypeCsv } from "react-bootstrap-icons";
import { LineChart, XAxis, CartesianGrid, Line, YAxis, Legend, Tooltip, ResponsiveContainer } from "recharts";
import { ReuseCheckResult, ComplianceStatus, ReuseParameterCheckResult, SamplingParameter, SamplingPointType, WastewaterTreatementPlant, WastewaterTreatementPlantEnvironmentalPermit, WastewaterTreatementPlantEnvironmentalPermitLimit, WastewaterTreatementPlantSamplingPoint } from "../../data/Definitions";
import { useTranslation } from 'react-i18next';
import { Formik, useFormikContext } from "formik";
import * as yup from 'yup';
import Select from 'react-select';
import MeasurementsFromFile from "../../components/MeasurementsFromFile";
import SamplerPositionsModal from "../../components/SamplerPositionsModal";
import moment from "moment";
import useFetch from "use-http";
import { AppContext } from "../../state/State";

const colors = ["#3366cc", "#dc3912", "#ff9900", "#109618", "#990099", "#0099c6", "#66aa00", "#b82e2e", "#316395", "#3366cc", "#994499", "#22aa99", "#aaaa11", "#6633cc", "#e67300", "#8b0707", "#651067", "#329262", "#5574a6", "#3b3eac", "#b77322", "#16d620", "#b91383", "#f4359e", "#9c5935", "#a9c413", "#2a778d", "#668d1c", "#bea413", "#0c5922", "#743411"];

const WtpManageMeasurementsReuse: React.FC<{ samplingParameters: SamplingParameter[], samplingPointTypes: SamplingPointType[], setSamplingPointTypes: Function, shouldGetMeasurements?: boolean }> = ({ samplingParameters, samplingPointTypes, setSamplingPointTypes, shouldGetMeasurements }) => {
    const [selectedSamplingPointType, setSelectedSamplingPointType] = useState(samplingPointTypes[0]?.id ?? 0);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [filter, setFilter] = useState("all");
    const [yearFilter, setYearFilter] = useState<number>(-1);
    const [startDateFilter, setStartDateFilter] = useState<string | null>(null);
    const [endDateFilter, setEndDateFilter] = useState<string | null>(null);
    const [inputData, setInputData] = useState<WastewaterTreatementPlantSamplingPoint[][]>([]);
    // const [outputData, setOutputData] = useState<WastewaterTreatementPlantSamplingPoint[]>([]);
    const [inputDataReversed, setInputDataReversed] = useState<WastewaterTreatementPlantSamplingPoint[][]>([]);
    // const [outputDataReversed, setOutputDataReversed] = useState<WastewaterTreatementPlantSamplingPoint[]>([]);
    const { t, i18n } = useTranslation();
    const { handleChange, handleBlur, values, touched, errors, setFieldValue } = useFormikContext();
    const [addSamplingPointModalShow, setAddSamplingPointModalShow] = useState(false);
    const addSamplingPointFormRef = useRef<any>(null);
    const addSamplingPointFormikRef = useRef<any>(null);
    const [editSamplingPointModalShow, setEditSamplingPointModalShow] = useState(false);
    const editSamplingPointFormRef = useRef<any>(null);
    const editSamplingPointFormikRef = useRef<any>(null);
    const [selectedSamplingPoint, setSelectedSamplingPoint] = useState<WastewaterTreatementPlantSamplingPoint | null>(null);
    const [visibleParametersInChart, setVisibleParametersInChart] = useState<string[]>([]);
    const [dataToDisplay, setDataToDisplay] = useState<WastewaterTreatementPlantSamplingPoint[]>([]);
    const [parametersInSamplingForm, setParametersInSamplingForm] = useState<{ id: number, parameterId: number, value: number }[]>([]);
    const [parametersInEditSamplingForm, setParametersInEditSamplingForm] = useState<{ id: number, parameterId: number, value: number }[]>([]);
    const [parametersInLimitsForm, setParametersInLimitsForm] = useState<{ id: number, parameterId: number, limitValue: number }[]>([]);
    const [sameParameterError, setSameParameterError] = useState(false);
    const [addEnvironmentalPermitModalShow, setAddEnvironmentalPermitModalShow] = useState(false);
    const addEnvironmentalPermitFormRef = useRef<any>(null);
    const addEnvironmentalPermitFormikRef = useRef<any>(null);
    const [editEnvironmentalPermitModalShow, setEditEnvironmentalPermitModalShow] = useState(false);
    const editEnvironmentalPermitFormRef = useRef<any>(null);
    const [selectedEnvironmentalPermit, setSelectedEnvironmentalPermit] = useState<WastewaterTreatementPlantEnvironmentalPermit | null>(null);
    const [addSamplingPointFileModalShow, setAddSamplingPointFileModalShow] = useState(false);
    const [samplingModalShow, setSamplingModalShow] = useState(false);
    const [checkComplianceModalShow, setCheckComplianceModalShow] = useState(false);
    const [complianceReuseEuResults, setComplianceReuseEuResults] = useState<ReuseCheckResult[]>([]);
    const [complianceReuseKyaResults, setComplianceReuseKyaResults] = useState<ReuseCheckResult[]>([]);
    const { get, post, response, loading, error, cache } = useFetch('/api');
    const context = useContext(AppContext);
    
    const shouldBeDateLocked = (date?: string | null) => {
        if (!date) return true;
        return (
            new Date() >=
            new Date(
                `${new Date(date).getFullYear() + 1}-06-01 00:00`
            )
        );
    }

    const isReporterOrAdmin = useMemo(() => {
        return (
            context.state.loggedIn &&
            context.state.userInfo?.roles.findIndex(
                (x) => x.toLowerCase() === "reporter" ||
                x.toLowerCase() === "sysadmin"
            ) !== -1
        );
    }, [context.state.loggedIn, context.state.userInfo]);

    const samplingSchema = yup.object().shape(Object.assign(
        {
            samplingDate: yup.date().required(t("wtp.validation.required")).min(new Date("2000"), t("wtp.validation.date_invalid")).max(new Date(), t("wtp.validation.date_invalid")).typeError(t("wtp.validation.type_error")),
            samplingPointTypeId: yup.number().required(t("wtp.validation.required")).oneOf(samplingPointTypes.map(x => x.id), t("wtp.validation.no_select")).typeError(t("wtp.validation.type_error")),
        },
        Object.fromEntries(parametersInSamplingForm.map((x, i) => [`parameter_${i}`, yup.number().required(t("wtp.validation.required")).oneOf(Array.from({ length: samplingParameters.length }, (item, index) => index + 1), t("wtp.validation.no_select")).typeError(t("wtp.validation.type_error")),])),
        Object.fromEntries(parametersInSamplingForm.map((x, i) => [`parameter_value_${i}`, yup.number().nullable().min(0.0, (v) => `${t("wtp.validation.greater_than")} ${v.min.toFixed(2)}`).typeError(t("wtp.validation.type_error")),]))
    ));

    const limitsSchema = yup.object().shape(Object.assign(
        {
            dateIssued: yup.date().required(t("wtp.validation.required")).typeError(t("wtp.validation.type_error")),
            dateExpired: yup.date().required(t("wtp.validation.required")).typeError(t("wtp.validation.type_error")),
            samplingPointTypeId: yup.number().required(t("wtp.validation.required")).oneOf(samplingPointTypes.map(x => x.id), t("wtp.validation.no_select")).typeError(t("wtp.validation.type_error")),
            comments: yup.string().nullable().typeError(t("wtp.validation.type_error")),
        },
        Object.fromEntries(parametersInLimitsForm.map((x, i) => [`parameter_${i}`, yup.number().required(t("wtp.validation.required")).oneOf(Array.from({ length: samplingParameters.length }, (item, index) => index + 1), t("wtp.validation.no_select")).typeError(t("wtp.validation.type_error")),])),
        Object.fromEntries(parametersInLimitsForm.map((x, i) => [`parameter_value_${i}`, yup.number().nullable().min(0.0, (v) => `${t("wtp.validation.greater_than")} ${v.min.toFixed(2)}`).typeError(t("wtp.validation.type_error")),]))
    ));

    useEffect(() => {
        if (selectedSamplingPointType === 0)
            setSelectedSamplingPointType(samplingPointTypes[0]?.id ?? 0);
    }, [samplingPointTypes])

    useEffect(() => {
        handleMeasurementsData();
    }, [filter, yearFilter, startDateFilter, endDateFilter])

    useEffect(() => {
        if (!addSamplingPointFileModalShow) {
            handleMeasurementsData();
        }
    }, [addSamplingPointFileModalShow])

    useEffect(() => {
        if (!addSamplingPointModalShow) {
            setParametersInSamplingForm([]);
            setSameParameterError(false);
            handleMeasurementsData();
        }
    }, [addSamplingPointModalShow])

    useEffect(() => {
        if (!editSamplingPointModalShow) {
            setSelectedSamplingPoint(null);
            setParametersInSamplingForm([]);
            setSameParameterError(false);
            handleMeasurementsData();
        }
    }, [editSamplingPointModalShow])

    useEffect(() => {
        if (!addEnvironmentalPermitModalShow) {
            setParametersInLimitsForm([]);
            setSameParameterError(false);
        }
    }, [addEnvironmentalPermitModalShow])

    useEffect(() => {
        if (!editEnvironmentalPermitModalShow) {
            setSelectedEnvironmentalPermit(null);
            setParametersInLimitsForm([]);
            setSameParameterError(false);
        }
    }, [editEnvironmentalPermitModalShow])

    useEffect(() => {
        if (inputData.length === 0) {
            setDataToDisplay([]);
        } else {
            let arr = inputData[selectedSamplingPointType] ? [...inputData[selectedSamplingPointType]] : [];
            arr.sort((a, b) => new Date(b.samplingDate!).getTime() - new Date(a.samplingDate!).getTime());
            let data = arr.slice((currentPage - 1) * entriesPerPage, Math.min(currentPage * entriesPerPage, arr.length))
            setDataToDisplay(data);
        }
    }, [inputData, currentPage, entriesPerPage, selectedSamplingPointType])

    useLayoutEffect(() => {
        if (shouldGetMeasurements === true) getMeasurements();
    }, [shouldGetMeasurements])

    useEffect(() => {
        if (checkComplianceModalShow) {
            getCompliance();
        } else {
            setComplianceReuseEuResults([]);
            setComplianceReuseKyaResults([]);
        }
    }, [checkComplianceModalShow])

    useLayoutEffect(() => {
        getMeasurements();
    }, [])

    const getCompliance = async () => {
        let clrs = await get(`/compliance/${(values as any).code}/reuse_eu`);
        if (response.ok) {
            setComplianceReuseEuResults(clrs);
        }

        // let cars = await get(`/compliance/${(values as any).code}/reuse_kya`);
        // if (response.ok) {
        //     setComplianceReuseKyaResults(cars);
        // }
    }

    const recalculateReuseKya = async () => {
        cache.delete(`url:/api/compliance/${(values as any).code}/reuse_kya||method:POST||body:`);
        await post(`/compliance/${(values as any).code}/reuse_kya`);
        if (response.ok) {
            cache.delete(`url:/api/compliance/${(values as any).code}/reuse_kya||method:GET||body:`);
            await getCompliance();
        }
    }

    const recalculateReuseEu = async () => {
        cache.delete(`url:/api/compliance/${(values as any).code}/reuse_eu||method:POST||body:`);
        await post(`/compliance/${(values as any).code}/reuse_eu`);
        if (response.ok) {
            cache.delete(`url:/api/compliance/${(values as any).code}/reuse_eu||method:GET||body:`);
            await getCompliance();
        }
    }

    const getMeasurements = async () => {
        cache.delete(`url:/api/wastewatertreatmentplant/${(values as any).id}/measurements||method:GET||body:`);
        let ms = await get(`/wastewatertreatmentplant/${(values as any).id}/measurements`);
        if (response.ok) {
            setFieldValue("samplingPoints", ms);
            handleMeasurementsData(ms);
            return ms;
        }
        if (error) {
            setFieldValue("samplingPoints", []);
            return [];
        }
        return [];
    }

    const handleMeasurementsData = (inM?: any[]) => {
        let measurements = (values as any).samplingPoints;
        if (!measurements) {
            measurements = inM;
        }
        if (!measurements) {
            return
        }

        let data = measurements.filter((x: WastewaterTreatementPlantSamplingPoint) => x.isReuse).sort((a: WastewaterTreatementPlantSamplingPoint, b: WastewaterTreatementPlantSamplingPoint) => { return (new Date(b.samplingDate!).getTime() - new Date(a.samplingDate!).getTime()) });
        switch (filter) {
            case "year":
                if (yearFilter >= 0) {
                    data = data.filter((x: WastewaterTreatementPlantSamplingPoint) => new Date(x.samplingDate!).getFullYear() === yearFilter);
                }
                break;
            case "range":
                if (startDateFilter || endDateFilter) {
                    let start: string | null = startDateFilter;
                    let end: string | null = endDateFilter;

                    if (!start) start = data[data.length - 1].samplingDate;
                    if (!end) end = data[0].samplingDate;

                    data = data.filter((x: WastewaterTreatementPlantSamplingPoint) => new Date(x.samplingDate!) >= new Date(start!));
                    data = data.filter((x: WastewaterTreatementPlantSamplingPoint) => new Date(x.samplingDate!) <= new Date(end!));
                }
                break;
            default:
                break;
        }

        let inputSamples = [];
        let inputSamplesReversed = [];
        for (var samplingpointType of samplingPointTypes) {
            let tempData = data.filter((x: WastewaterTreatementPlantSamplingPoint) => x.samplingPointTypeId === samplingpointType.id);
            inputSamples[samplingpointType.id] = tempData;
            inputSamplesReversed[samplingpointType.id] = tempData.reverse();
        }

        setInputData([...inputSamples]);
        setInputDataReversed([...inputSamplesReversed]);
        setVisibleParametersInChart(samplingParameters.slice(0, 3).map(x => x.name));
    }

    const getWaterQuality = (parameterId: number, yearResults: ReuseParameterCheckResult[]) => {
        var r = yearResults.find(x => x.parameterId === parameterId);
        if (!r) return "❔";
        switch (r.waterQuality) {
            case 0: return i18n.language === "en" ? "A" : "Α";
            case 1: return i18n.language === "en" ? "B" : "Β";
            case 2: return i18n.language === "en" ? "C" : "Γ";
            case 3: return i18n.language === "en" ? "D" : "Δ";
            case null: return "󠁼➖";
            default: return "❔";
        }
    }

    const getTotalWaterQuality = (yearResults: ReuseParameterCheckResult[]) => {
        var r = yearResults.map(x => x.waterQuality);
        var f = r.filter(x => x !== null && x !== undefined) as number[];
        var waterQuality = r.some(x => x === null || x === undefined) ? null : Math.max(...f);
        switch (waterQuality) {
            case 0: return i18n.language === "en" ? "A" : "Α";
            case 1: return i18n.language === "en" ? "B" : "Β";
            case 2: return i18n.language === "en" ? "C" : "Γ";
            case 3: return i18n.language === "en" ? "D" : "Δ";
            case null: return "󠁼➖";
            default: return "❔";
        }
    }

    const getPagination = () => {
        let rows;
        if (inputData.length === 0) {
            rows = 0;
        } else {
            rows = inputData[selectedSamplingPointType]?.length ?? 0;
        }

        let page = currentPage,
            between = 3,
            total = rows,
            limit = entriesPerPage ?? 10,
            changePage = (page: number) => setCurrentPage(page),
            next = true,
            last = true,
            ellipsis = 3;

        const total_pages = Math.ceil(total / limit)
        between = between < 1 ? 1 : between
        page = (page < 1 ? 1 : page > total_pages ? total_pages : page)
        ellipsis = ellipsis < 1 ? 0 : ellipsis + 2 >= between ? between - 2 : ellipsis

        let positions = Array.from({ length: total_pages }, (_, i) => i)

        const qtd_pages = (between * 2) + 1
        const range = (
            total_pages <= qtd_pages
                // Show active without slice
                ? positions
                : page - 1 <= between
                    // Show active in left
                    ? positions.slice(0, qtd_pages - (ellipsis > 0 ? ellipsis + 1 : 0))
                    : page + between >= total_pages
                        // Show active in right
                        ? positions.slice(total_pages - qtd_pages + (ellipsis > 0 ? ellipsis + 1 : 0), total_pages)
                        // Show active in middle
                        : positions.slice((page - 1) - (between - (ellipsis > 0 ? ellipsis + 1 : 0)), page + (between - (ellipsis > 0 ? ellipsis + 1 : 0)))
        )

        return total !== null && total > 0
            ? <Form.Group as={Row} className="mb-2 me-1 justify-content-between">
                <Form.Label column>{t("wtp.pagination.page")} <strong>{currentPage}</strong> {t("wtp.pagination.of")} <strong>{total_pages}</strong> ({rows} {t("wtp.pagination.entries")})</Form.Label>
                <Col className="pe-0">
                    <Pagination>
                        {
                            last
                            && <Pagination.First
                                onClick={() => page > 1 ? changePage(1) : {}}
                                disabled={page <= 1} />
                        }
                        {
                            next
                            && <Pagination.Prev
                                onClick={() => page > 1 ? changePage(page - 1) : {}}
                                disabled={page <= 1} />
                        }
                        {
                            total_pages > (between * 2) + 1 && ellipsis > 0
                            && positions.slice(0, page - 1 <= between ? 0 : ellipsis).map(value => {
                                return <Pagination.Item key={value}
                                    onClick={() => value !== page - 1 ? changePage(value + 1) : {}}>
                                    {value + 1}
                                </Pagination.Item>
                            })
                        }
                        {
                            // Show ellipsis when "page" is bigger than "between"
                            total_pages > (between * 2) + 1 && ellipsis > 0 && page - 1 > between
                            && <Pagination.Ellipsis disabled />
                        }
                        {range.map(value => {
                            return <Pagination.Item active={value === page - 1}
                                key={value}
                                onClick={() => value !== page - 1 ? changePage(value + 1) : {}}>
                                {value + 1}
                            </Pagination.Item>
                        })}
                        {
                            // Show ellipsis when "page" is lower than "between"
                            total_pages > (between * 2) + 1 && ellipsis > 0 && page < total_pages - between
                            && <Pagination.Ellipsis disabled />
                        }
                        {
                            total_pages > (between * 2) + 1 && ellipsis > 0
                            && positions.slice(page >= total_pages - between ? total_pages : total_pages - ellipsis, total_pages).map(value => {
                                return <Pagination.Item key={value}
                                    onClick={() => value !== page - 1 ? changePage(value + 1) : {}}>
                                    {value + 1}
                                </Pagination.Item>
                            })
                        }
                        {
                            next
                            && <Pagination.Next
                                onClick={() => page < total_pages ? changePage(page + 1) : {}}
                                disabled={page >= total_pages} />
                        }
                        {
                            last
                            && <Pagination.Last
                                onClick={() => page < total_pages ? changePage(total_pages) : {}}
                                disabled={page >= total_pages} />
                        }
                    </Pagination>
                </Col>
                <Col className="pe-0">
                    <Form.Group as={Row} className="mb-2 me-2">
                        <Form.Label column className="text-end" md={{ span: 4, offset: 5 }}>{t("wtp.pagination.entries_per_page")}</Form.Label>
                        <Col md={{ span: 3 }} className="pe-0">
                            <Form.Select aria-label="Επιλογή" onChange={v => { setEntriesPerPage(parseInt(v.target.value)); setCurrentPage(1); }}>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </Form.Select>
                        </Col>
                    </Form.Group>
                </Col>
            </Form.Group>
            : <></>
    }

    const handleAddSamplingPointSubmit = (val: any) => {
        if (sameParameterError) {
            return;
        }
        let cur = (values as any).samplingPoints;
        let temp = samplingParameters.map(x => {
            return {
                id: x.id,
                name: x.name,
                unit: x.unit,
                description: x.description,
                value: (parametersInSamplingForm.find(y => y.parameterId === x.id)?.value === null || parametersInSamplingForm.find(y => y.parameterId === x.id)?.value === undefined) ? null : parametersInSamplingForm.find(y => y.parameterId === x.id)?.value,
            }
        }).filter(x => x.value !== null && x.value !== undefined);
        const samplingPointTypeId = parseInt(val.samplingPointTypeId.toString());
        cur = cur.filter((x: any) => !(moment(x.samplingDate).format('L') === moment(val.samplingDate).format('L') && x.samplingPointTypeId === samplingPointTypeId && x.isReuse === true));
        cur.push({
            samplingDate: val.samplingDate.toString(),
            samplingPointTypeId: samplingPointTypeId,
            isReuse: true,
            samples: temp as SamplingParameter[],
        });
        let data = cur.sort((a: WastewaterTreatementPlantSamplingPoint, b: WastewaterTreatementPlantSamplingPoint) => { return (new Date(b.samplingDate!).getTime() - new Date(a.samplingDate!).getTime()) });
        let inputSamples = [];
        let inputSamplesReversed = [];
        for (var samplingpointType of samplingPointTypes) {
            let tempData = data.filter((x: WastewaterTreatementPlantSamplingPoint) => x.samplingPointTypeId === samplingpointType.id);
            inputSamples[samplingpointType.id] = tempData;
            inputSamplesReversed[samplingpointType.id] = tempData.reverse();
        }

        setInputData([...inputSamples]);
        setInputDataReversed([...inputSamplesReversed]);
        setFieldValue("samplingPoints", cur);
        setAddSamplingPointModalShow(false);
        setSelectedSamplingPoint(null);
    };

    const handleEditSamplingPointSubmit = (val: any) => {
        if (!selectedSamplingPoint) {
            return;
        }
        if (sameParameterError) {
            return;
        }
        let cur = (values as any).samplingPoints;
        let temp = samplingParameters.map(x => {
            return {
                id: x.id,
                name: x.name,
                unit: x.unit,
                description: x.description,
                value: (parametersInEditSamplingForm.find(y => y.parameterId === x.id)?.value === null || parametersInEditSamplingForm.find(y => y.parameterId === x.id)?.value === undefined) ? null : parametersInEditSamplingForm.find(y => y.parameterId === x.id)?.value,
            }
        }).filter(x => x.value !== null && x.value !== undefined);
        const samplingPointTypeId = parseInt(val.samplingPointTypeId.toString());
        cur = cur.filter((x: any) => !(moment(x.samplingDate).format('L') === moment(val.samplingDate).format('L') && x.samplingPointTypeId === samplingPointTypeId && x.isReuse === true));
        cur.push({
            samplingDate: val.samplingDate.toString(),
            samplingPointTypeId: samplingPointTypeId,
            isReuse: true,
            samples: temp as SamplingParameter[],
        });
        let data = cur.sort((a: WastewaterTreatementPlantSamplingPoint, b: WastewaterTreatementPlantSamplingPoint) => { return (new Date(b.samplingDate!).getTime() - new Date(a.samplingDate!).getTime()) });
        let inputSamples = [];
        let inputSamplesReversed = [];
        for (var samplingpointType of samplingPointTypes) {
            let tempData = data.filter((x: WastewaterTreatementPlantSamplingPoint) => x.samplingPointTypeId === samplingpointType.id);
            inputSamples[samplingpointType.id] = tempData;
            inputSamplesReversed[samplingpointType.id] = tempData.reverse();
        }

        setInputData([...inputSamples]);
        setInputDataReversed([...inputSamplesReversed]);
        setFieldValue("samplingPoints", cur);
        setEditSamplingPointModalShow(false);
        setSelectedSamplingPoint(null);
    };

    // const handleEditSamplingPointSubmit = (val: any) => {
    //     if (!selectedSamplingPoint) return;
    //     let cur = (values as any).samplingPoints;
    //     let idxToEdit = cur.findIndex((x: WastewaterTreatementPlantSamplingPoint) => x === selectedSamplingPoint);
    //     if (idxToEdit === -1) return;
    //     let temp = samplingParameters.map(x => {
    //         return {
    //             id: x.id,
    //             name: x.name,
    //             unit: x.unit,
    //             description: x.description,
    //             value: parseFloat(val[x.name!].toString()) ?? null,
    //         }
    //     }).filter(x => x.value);
    //     let samples: SamplingParameter[] = temp as SamplingParameter[];
    //     cur[idxToEdit] = Object.assign(selectedSamplingPoint, {
    //         samplingDate: val.samplingDate.toString(),
    //         samplingPointTypeId: parseInt(val.samplingPointTypeId.toString()),
    //         isReuse: true,
    //         samples: samples,
    //     });
    //     let data = cur.sort((a: WastewaterTreatementPlantSamplingPoint, b: WastewaterTreatementPlantSamplingPoint) => { return (new Date(b.samplingDate!).getTime() - new Date(a.samplingDate!).getTime()) });
    //     let inputSamples = [];
    //     let inputSamplesReversed = [];
    //     for (var samplingpointType of samplingPointTypes) {
    //         let tempData = data.filter((x: WastewaterTreatementPlantSamplingPoint) => x.samplingPointTypeId === samplingpointType.id);
    //         inputSamples[samplingpointType.id] = tempData;
    //         inputSamplesReversed[samplingpointType.id] = tempData.reverse();
    //     }

    //     setInputData([...inputSamples]);
    //     setInputDataReversed([...inputSamplesReversed]);
    //     setFieldValue("samplingPoints", cur);
    //     setEditSamplingPointModalShow(false);
    //     setSelectedSamplingPoint(null);
    // };

    const removeSamplingPoint = () => {
        if (!selectedSamplingPoint) return;
        let cur = (values as any).samplingPoints;
        let idxToRemove = cur.findIndex((x: WastewaterTreatementPlantSamplingPoint) => x === selectedSamplingPoint);
        if (idxToRemove === -1) return;
        cur.splice(idxToRemove, 1);
        let data = cur.sort((a: WastewaterTreatementPlantSamplingPoint, b: WastewaterTreatementPlantSamplingPoint) => { return (new Date(b.samplingDate!).getTime() - new Date(a.samplingDate!).getTime()) });
        let inputSamples = [];
        let inputSamplesReversed = [];
        for (var samplingpointType of samplingPointTypes) {
            let tempData = data.filter((x: WastewaterTreatementPlantSamplingPoint) => x.samplingPointTypeId === samplingpointType.id);
            inputSamples[samplingpointType.id] = tempData;
            inputSamplesReversed[samplingpointType.id] = tempData.reverse();
        }

        setInputData([...inputSamples]);
        setInputDataReversed([...inputSamplesReversed]);
        setFieldValue("samplingPoints", cur);
        setSelectedSamplingPoint(null);
        handleMeasurementsData();
    }

    const handleAddEnvironmentalPermitSubmit = (val: any) => {
        if (sameParameterError) {
            return;
        }
        let cur = (values as any).environmentalPermits;
        let temp = samplingParameters.map(x => {
            return {
                id: x.id,
                name: x.name,
                limitValue: parametersInLimitsForm.find(y => y.parameterId === x.id)?.limitValue ?? null,
            }
        }).filter(x => x.limitValue);
        cur = cur.filter((x: any) => moment(x.samplingDate).format('L') !== moment(val.samplingDate).format('L'));
        cur.push({
            dateIssued: val.dateIssued.toString(),
            dateExpired: val.dateExpired.toString(),
            samplingPointTypeId: parseInt(val.samplingPointTypeId.toString()),
            comments: val.comments,
            limits: temp as WastewaterTreatementPlantEnvironmentalPermitLimit[],
        });
        setFieldValue("environmentalPermits", cur);
        setAddEnvironmentalPermitModalShow(false);
        setSelectedEnvironmentalPermit(null);
    };

    const handleEditEnvironmentalPermitSubmit = (val: any) => {
        if (!selectedEnvironmentalPermit) return;
        let cur = (values as any).environmentalPermits;
        let idxToEdit = cur.findIndex((x: WastewaterTreatementPlantEnvironmentalPermit) => x === selectedEnvironmentalPermit);
        if (idxToEdit === -1) return;
        let temp = samplingParameters.map(x => {
            return {
                id: x.id,
                name: x.name,
                limitValue: parseFloat(val[x.name!].toString()) ?? null,
            }
        }).filter(x => x.limitValue);
        cur[idxToEdit] = Object.assign(selectedEnvironmentalPermit, {
            dateIssued: val.dateIssued.toString(),
            dateExpired: val.dateExpired.toString(),
            samplingPointTypeId: parseInt(val.samplingPointTypeId.toString()),
            comments: val.comments,
            samples: temp as WastewaterTreatementPlantEnvironmentalPermitLimit[],
        });
        setFieldValue("environmentalPermits", cur);
        setEditEnvironmentalPermitModalShow(false);
        setSelectedEnvironmentalPermit(null);
    };

    const removeEnvironmentalPermit = () => {
        if (!selectedEnvironmentalPermit) return;
        let cur = (values as any).environmentalPermits;
        let idxToRemove = cur.findIndex((x: WastewaterTreatementPlantEnvironmentalPermit) => x === selectedEnvironmentalPermit);
        if (idxToRemove === -1) return;
        cur.splice(idxToRemove, 1);
        setFieldValue("environmentalPermits", cur);
        setSelectedEnvironmentalPermit(null);
    }

    const changeParameterVisibility = (val: string) => {
        if (!val) return;
        let idxToRemove = visibleParametersInChart.indexOf(val);
        let arr = [...visibleParametersInChart];
        if (idxToRemove === -1) {
            arr.push(val);
        } else {
            arr.splice(idxToRemove, 1);
        }
        setVisibleParametersInChart(arr);
    }

    const addParameterInSamplingForm = () => {
        if (parametersInSamplingForm.length >= samplingParameters.length) return;
        let cur = [...parametersInSamplingForm];
        let id;
        if (cur.length > 0) {
            let curIds = cur.map(x => x.id) || [0];
            id = Math.max(...curIds) + 1;
        } else {
            id = 1;
        }
        cur.push({
            id: id,
            parameterId: 0,
            value: 0,
        });
        setParametersInSamplingForm(cur);
        addSamplingPointFormikRef.current?.setErrors({});
        addSamplingPointFormikRef.current?.setTouched({});
    }

    const removeParameterInSamplingForm = (id: number) => {
        if (!id) return;
        let idxToRemove = parametersInSamplingForm.findIndex(x => x.id === id);
        if (idxToRemove === -1) return;
        let cur = [...parametersInSamplingForm];
        cur.splice(idxToRemove, 1);
        setParametersInSamplingForm(cur);
        if (addSamplingPointFormikRef.current?.submitCount > 0) {
            let val = Object.assign(
                {
                    samplingDate: addSamplingPointFormikRef.current?.values.samplingDate,
                    samplingPointTypeId: addSamplingPointFormikRef.current?.values.samplingPointTypeId,
                },
                Object.fromEntries(samplingParameters.map((x, i) => [`parameter_${i}`, cur[i]?.parameterId ?? 0])),
                Object.fromEntries(samplingParameters.map((x, i) => [`parameter_value_${i}`, cur[i]?.value ?? 0]))
            );
            addSamplingPointFormikRef.current?.setValues(val);
        }
        var valueArr = cur.filter(x => x.parameterId !== 0).map(x => x.parameterId);
        var isDuplicate = valueArr.some(function (x, idx) {
            return valueArr.indexOf(x) !== idx;
        });
        if (isDuplicate) {
            setSameParameterError(true);
        } else {
            setSameParameterError(false);
        }
        addSamplingPointFormikRef.current?.setErrors({});
        addSamplingPointFormikRef.current?.setTouched({});
    }

    const changeParameterInSamplingForm = (id: number, val: string) => {
        if (!id) return;
        if (!val) return;
        let idxToEdit = parametersInSamplingForm.findIndex(x => x.id === id);
        if (idxToEdit === -1) return;
        let cur = [...parametersInSamplingForm];
        cur[idxToEdit].parameterId = parseInt(val);
        var valueArr = cur.filter(x => x.parameterId !== 0).map(x => x.parameterId);
        var isDuplicate = valueArr.some(function (x, idx) {
            return valueArr.indexOf(x) !== idx;
        });
        if (isDuplicate) {
            setSameParameterError(true);
        } else {
            setSameParameterError(false);
        }
        setParametersInSamplingForm(cur);
    }

    const changeParameterValueInSamplingForm = (id: number, val: string) => {
        if (!id) return;
        let idxToEdit = parametersInSamplingForm.findIndex(x => x.id === id);
        if (idxToEdit === -1) return;
        let cur = [...parametersInSamplingForm];
        cur[idxToEdit].value = parseFloat(val) ?? 0;
        setParametersInSamplingForm(cur);
    }

    const addParameterInEditSamplingForm = () => {
        if (parametersInEditSamplingForm.length >= samplingParameters.length) return;
        let cur = [...parametersInEditSamplingForm];
        let id;
        if (cur.length > 0) {
            let curIds = cur.map(x => x.id) || [0];
            id = Math.max(...curIds) + 1;
        } else {
            id = 1;
        }
        cur.push({
            id: id,
            parameterId: 0,
            value: 0,
        });
        setParametersInEditSamplingForm(cur);
        editSamplingPointFormikRef.current?.setErrors({});
        editSamplingPointFormikRef.current?.setTouched({});
    }

    const removeParameterInEditSamplingForm = (id: number) => {
        if (!id) return;
        let idxToRemove = parametersInEditSamplingForm.findIndex(x => x.id === id);
        if (idxToRemove === -1) return;
        let cur = [...parametersInEditSamplingForm];
        cur.splice(idxToRemove, 1);
        setParametersInEditSamplingForm(cur);
        if (editSamplingPointFormikRef.current?.submitCount > 0) {
            let val = Object.assign(
                {
                    samplingDate: editSamplingPointFormikRef.current?.values.samplingDate,
                    samplingPointTypeId: editSamplingPointFormikRef.current?.values.samplingPointTypeId,
                },
                Object.fromEntries(samplingParameters.map((x, i) => [`parameter_${i}`, cur[i]?.parameterId ?? 0])),
                Object.fromEntries(samplingParameters.map((x, i) => [`parameter_value_${i}`, cur[i]?.value ?? 0]))
            );
            editSamplingPointFormikRef.current?.setValues(val);
        }
        var valueArr = cur.filter(x => x.parameterId !== 0).map(x => x.parameterId);
        var isDuplicate = valueArr.some(function (x, idx) {
            return valueArr.indexOf(x) !== idx;
        });
        if (isDuplicate) {
            setSameParameterError(true);
        } else {
            setSameParameterError(false);
        }
        editSamplingPointFormikRef.current?.setErrors({});
        editSamplingPointFormikRef.current?.setTouched({});
    }

    const changeParameterInEditSamplingForm = (id: number, val: string) => {
        if (!id) return;
        if (!val) return;
        let idxToEdit = parametersInEditSamplingForm.findIndex(x => x.id === id);
        if (idxToEdit === -1) return;
        let cur = [...parametersInEditSamplingForm];
        cur[idxToEdit].parameterId = parseInt(val);
        var valueArr = cur.filter(x => x.parameterId !== 0).map(x => x.parameterId);
        var isDuplicate = valueArr.some(function (x, idx) {
            return valueArr.indexOf(x) !== idx;
        });
        if (isDuplicate) {
            setSameParameterError(true);
        } else {
            setSameParameterError(false);
        }
        setParametersInEditSamplingForm(cur);
    }

    const changeParameterValueInEditSamplingForm = (id: number, val: string) => {
        if (!id) return;
        let idxToEdit = parametersInEditSamplingForm.findIndex(x => x.id === id);
        if (idxToEdit === -1) return;
        let cur = [...parametersInEditSamplingForm];
        cur[idxToEdit].value = parseFloat(val) ?? 0;
        setParametersInEditSamplingForm(cur);
    }

    const addParameterInLimitsForm = () => {
        if (parametersInLimitsForm.length >= samplingParameters.length) return;
        let cur = [...parametersInLimitsForm];
        let id;
        if (cur.length > 0) {
            let curIds = cur.map(x => x.id) || [0];
            id = Math.max(...curIds) + 1;
        } else {
            id = 1;
        }
        cur.push({
            id: id,
            parameterId: 0,
            limitValue: 0,
        });
        setParametersInLimitsForm(cur);
        addEnvironmentalPermitFormikRef.current?.setErrors({});
        addEnvironmentalPermitFormikRef.current?.setTouched({});
    }

    const removeParameterInLimitsForm = (id: number) => {
        if (!id) return;
        let idxToRemove = parametersInLimitsForm.findIndex(x => x.id === id);
        if (idxToRemove === -1) return;
        let cur = [...parametersInLimitsForm];
        cur.splice(idxToRemove, 1);
        setParametersInLimitsForm(cur);
        if (addEnvironmentalPermitFormikRef.current?.submitCount > 0) {
            let val = Object.assign(
                {
                    dateIssued: addEnvironmentalPermitFormikRef.current?.values.dateIssued,
                    dateExpired: addEnvironmentalPermitFormikRef.current?.values.dateExpired,
                    samplingPointTypeId: addEnvironmentalPermitFormikRef.current?.values.samplingPointTypeId,
                    comments: addEnvironmentalPermitFormikRef.current?.values.comments,
                },
                Object.fromEntries(samplingParameters.map((x, i) => [`parameter_${i}`, cur[i]?.parameterId ?? 0])),
                Object.fromEntries(samplingParameters.map((x, i) => [`parameter_value_${i}`, cur[i]?.limitValue ?? 0]))
            );
            addEnvironmentalPermitFormikRef.current?.setValues(val);
        }
        var valueArr = cur.filter(x => x.parameterId !== 0).map(x => x.parameterId);
        var isDuplicate = valueArr.some(function (x, idx) {
            return valueArr.indexOf(x) !== idx;
        });
        if (isDuplicate) {
            setSameParameterError(true);
        } else {
            setSameParameterError(false);
        }
        addEnvironmentalPermitFormikRef.current?.setErrors({});
        addEnvironmentalPermitFormikRef.current?.setTouched({});
    }

    const changeParameterInLimitsForm = (id: number, val: string) => {
        if (!id) return;
        if (!val) return;
        let idxToEdit = parametersInLimitsForm.findIndex(x => x.id === id);
        if (idxToEdit === -1) return;
        let cur = [...parametersInLimitsForm];
        cur[idxToEdit].parameterId = parseInt(val);
        var valueArr = cur.filter(x => x.parameterId !== 0).map(x => x.parameterId);
        var isDuplicate = valueArr.some(function (x, idx) {
            return valueArr.indexOf(x) !== idx;
        });
        if (isDuplicate) {
            setSameParameterError(true);
        } else {
            setSameParameterError(false);
        }
        setParametersInLimitsForm(cur);
    }

    const changeParameterValueInLimitsForm = (id: number, val: string) => {
        if (!id) return;
        let idxToEdit = parametersInLimitsForm.findIndex(x => x.id === id);
        if (idxToEdit === -1) return;
        let cur = [...parametersInLimitsForm];
        cur[idxToEdit].limitValue = parseFloat(val) ?? 0;
        setParametersInLimitsForm(cur);
    }

    const generateComplianceTable = (results: ReuseCheckResult[]) => {
        const params = results.flatMap(x => x.results).map(x => x?.parameterId).filter((item, i, ar) => ar.indexOf(item) === i);
        return <Table striped bordered hover size="sm" className="mt-2">
            <thead>
                <tr>
                    <th className="text-center">{t("Year")}</th>
                    {params.map((x, i) => <th key={i} className="text-center">{samplingParameters.find(p => p.id === x)?.name}</th>)}
                    <th className="text-center">{t("wtp.measurements.compliance_reuse.total_water_quality")}</th>
                </tr>
            </thead>
            <tbody>
                {
                    results.sort((a, b) => (b.year ?? 0) - (a.year ?? 0)).map((x: ReuseCheckResult, idx: number) => <tr key={idx}>
                        <td className="text-center">{x.year}</td>
                        {params.map((y, i) => <td className="text-center">{getWaterQuality(y ?? 0, x.results ?? [])}</td>)}
                        <td className="text-center"><strong>{getTotalWaterQuality(x.results ?? [])}</strong></td>
                    </tr>)
                }
            </tbody>
        </Table>;
    }

    const getSamplingParametersToShow = useCallback((data: WastewaterTreatementPlantSamplingPoint[]) => {
        if (!data) return [];
        const params = data.flatMap(x => x.samples).flatMap(x => x.id).filter((item, i, ar) => ar.indexOf(item) === i);
        return samplingParameters.filter(x => params.includes(x.id));
    }, [samplingParameters]);

    const columns = useCallback((samplingPointType?: number) => {
        if (!samplingPointType) return [];
        return [
            {
                key: "samplingDate",
                name: t("wtp.measurements.sampling_results.date"),
                selector: (row: WastewaterTreatementPlantSamplingPoint) => row.samplingDate ? new Date(row.samplingDate).toLocaleDateString(i18n.language, { year: "numeric", month: "2-digit", day: "2-digit" }) : "N/A",
                center: true,
            },
            ...getSamplingParametersToShow(inputDataReversed[samplingPointType]).map(x => {
                return {
                    key: x.id.toString(),
                    name: `${x.name} (${x.unit})`,
                    selector: (row: WastewaterTreatementPlantSamplingPoint) => row.samples?.find(y => x.id === y.id)?.value?.toLocaleString(i18n.language) || "-",
                    center: true,
                };
            })
        ];
    }, [getSamplingParametersToShow, i18n.language, inputDataReversed, t])

    const convertArrayOfObjectsToCSV = (array: WastewaterTreatementPlantSamplingPoint[], cols: any) => {
        let result: any;

        const columnDelimiter = ';';
        const lineDelimiter = '\n';
        let header: string[] = cols.map((x: any) => x.name);
        header[0] = "Sampling date";

        // result = 'sep=' + columnDelimiter + lineDelimiter;
        result = '\ufeff';
        result += header.join(columnDelimiter);
        result += lineDelimiter;

        array.forEach(item => {
            let ctr = 0;
            let keys = cols.map((x: any) => x.key);
            keys.forEach((key: string) => {
                if (ctr > 0) result += columnDelimiter;

                if (ctr === 0) {
                    result += item.samplingDate ? new Date(item.samplingDate).toLocaleDateString("el-GR") : "";
                } else {
                    result += item.samples?.find(x => x.id?.toString() === key)?.value.toString().replace(".", ",") ?? "";
                }

                ctr++;
            });
            result += lineDelimiter;
        });

        return result;
    }

    const downloadCSV = (array: WastewaterTreatementPlantSamplingPoint[], cols: any) => {
        const link = document.createElement('a');
        let csv = convertArrayOfObjectsToCSV(array, cols);
        if (csv == null) return;

        const filename = 'export.csv';

        if (!csv.match(/^data:text\/csv/i)) {
            csv = `data:text/csv;charset=utf-8,${csv}`;
        }

        link.setAttribute('href', encodeURI(csv));
        link.setAttribute('download', filename);
        link.click();
    }

    const chart = useMemo(() => (!inputDataReversed[selectedSamplingPointType] || inputDataReversed[selectedSamplingPointType].length == 0) ? <div className="w-100 text-center mt-4">{t("wtp.table.no_data")}</div> : <ResponsiveContainer minHeight={700}>
        <LineChart data={inputDataReversed[selectedSamplingPointType]}>
            <CartesianGrid strokeDasharray="4 3" />
            <Legend verticalAlign="top" onClick={data => changeParameterVisibility(data.value)} />
            <Tooltip labelStyle={{ fontWeight: "bold" }} labelFormatter={x => new Date(x).toLocaleDateString(i18n.language === "en" ? "en-US" : "el-GR", { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })} />
            <XAxis dataKey={"samplingDate"} tickFormatter={(x: string, i: number) => new Date(x).toLocaleDateString(i18n.language === "en" ? "en-US" : "el-GR", { year: "numeric", month: "2-digit", day: "2-digit" })} />
            <YAxis />
            {getSamplingParametersToShow(inputDataReversed[selectedSamplingPointType]).map(x => <Line key={x.name} type="monotone" name={x.name} dataKey={y => y.samples.find((z: SamplingParameter) => z.id === x.id)?.value} stroke={colors[x.id]} unit={x.unit || undefined} connectNulls hide={visibleParametersInChart.indexOf(x.name) === -1} isAnimationActive={false} />)}
        </LineChart>
    </ResponsiveContainer>, [selectedSamplingPointType, inputDataReversed, samplingParameters, visibleParametersInChart, i18n.language]);

    const showEditSamplingPoint = () => {
        if (!selectedSamplingPoint) return;
        setParametersInEditSamplingForm(selectedSamplingPoint.samples.map((s, i, a) => {
            return {
                id: i + 1,
                parameterId: s.id,
                value: s.value,
            }
        }));
        editSamplingPointFormikRef.current?.setErrors({});
        editSamplingPointFormikRef.current?.setTouched({});
        setEditSamplingPointModalShow(true);
    }

    return (
        <>
            <Row>
                {/* <h4>{t("wtp.measurements.display_options.header")}</h4> */}
                <Col md={{ span: 2 }}>
                    <Row className="mb-2">
                        <Col className="pe-0 d-grid">
                            <Button variant="outline-info" className="d-flex align-items-center justify-content-center m-0" onClick={() => setSamplingModalShow(true)}>{t("wtp.measurements.sampling_positions")}</Button>
                        </Col>
                    </Row>
                    <Form.Group as={Row} className="mb-4">
                        <Form.Label column className="text-center" md={{ span: 12 }}>{t("wtp.measurements.display_options.sampler_position")}</Form.Label>
                        <Col md={{ span: 12 }} className="pe-0">
                            <Form.Select aria-label="Επιλογή" onChange={v => { setSelectedSamplingPointType(parseInt(v.target.value)); setCurrentPage(1) }}>
                                {
                                    samplingPointTypes.map((x: SamplingPointType, i: number) => <option key={i} value={x.id}>{i18n.language === "en" ? x.nameEn : x.name}</option>)
                                }
                            </Form.Select>
                        </Col>
                    </Form.Group>
                </Col>
                <Col md={{ span: 8 }}>
                    {chart}
                </Col>
                <Col md={{ span: 2 }}>
                    <Form.Group as={Row} className="mb-2 me-1">
                        <Form.Label column className="text-center" md={{ span: 12 }}>{t("wtp.measurements.display_options.display")}</Form.Label>
                        <Col md={{ span: 12 }} className="pe-0">
                            <Form.Select aria-label="Επιλογή" value={filter} onChange={v => { setFilter(v.target.value); setCurrentPage(1); }}>
                                <option key={1} value="all">{t("wtp.measurements.display_options.all")}</option>
                                <option key={2} value="range">{t("wtp.measurements.display_options.range")}</option>
                                <option key={3} value="year">{t("wtp.measurements.display_options.yearly")}</option>
                            </Form.Select>
                        </Col>
                    </Form.Group>
                    {filter === "range" && <>
                        <Form.Group as={Row} className="mb-2 me-1">
                            <Form.Label column className="text-center" md={{ span: 12 }}>{t("wtp.measurements.display_options.from")}</Form.Label>
                            <Col md={{ span: 12 }} className="pe-0">
                                <Form.Control type="date" value={startDateFilter ?? ""} onChange={v => { setStartDateFilter(v.target.value); setCurrentPage(1); }} />
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-2 me-1">
                            <Form.Label column className="text-center" md={{ span: 12 }}>{t("wtp.measurements.display_options.to")}</Form.Label>
                            <Col md={{ span: 12 }} className="pe-0">
                                <Form.Control type="date" value={endDateFilter ?? ""} onChange={v => { setEndDateFilter(v.target.value); setCurrentPage(1); }} />
                            </Col>
                        </Form.Group>
                    </>}
                    {filter === "year" &&
                        <Form.Group as={Row} className="mb-2 me-1">
                            <Form.Label column className="text-center" md={{ span: 12 }}>{t("wtp.measurements.display_options.year")}</Form.Label>
                            <Col md={{ span: 12 }} className="pe-0">
                                <Form.Select aria-label="Επιλογή" value={yearFilter ?? ""} onChange={v => { setYearFilter(parseInt(v.target.value)); setCurrentPage(1); }}>
                                    <option key={-1} value={-1}>{t("wtp.measurements.display_options.all")}</option>
                                    {(Array.from(new Set((values as any).samplingPoints.map((x: WastewaterTreatementPlantSamplingPoint) => new Date(x.samplingDate!).getFullYear()))) as number[]).sort().reverse().map((x) => <option key={x} value={x}>{x}</option>)}
                                </Form.Select>
                            </Col>
                        </Form.Group>}
                </Col>
            </Row>
            <hr></hr>
            {/* <Tabs
                defaultActiveKey="measurements"
                className="mb-3"
                transition={false}
            >
                <Tab eventKey="measurements" title={t("wtp.main.measurements")}> */}
            <Row>
                <Col>
                    <h4>{t("wtp.measurements.sampling_results.header")}</h4>
                </Col>
                <Col>
                    <ButtonGroup className='float-end btn-group-sm mt-2' aria-label="Toolbar">
                        <Button variant="outline-dark" className="d-flex align-items-center justify-content-center m-0" onClick={() => downloadCSV(inputDataReversed[selectedSamplingPointType], columns(selectedSamplingPointType))} disabled={!inputDataReversed || inputDataReversed.length === 0 || inputDataReversed[selectedSamplingPointType].length === 0}><FiletypeCsv className="me-2" /> Export to CSV</Button>
                        <Button variant="outline-info" className="d-flex align-items-center justify-content-center m-0" onClick={() => setCheckComplianceModalShow(true)}><Check2Circle className="me-2"></Check2Circle> {t("wtp.measurements.sampling_results.check_reuse")}</Button>
                        <Button variant="outline-primary" className="d-flex align-items-center justify-content-center m-0" onClick={() => setAddSamplingPointModalShow(true)}><PlusCircle className="me-2"></PlusCircle> {t("wtp.button.add_upper")}</Button>
                        <Button variant="outline-secondary" className="d-flex align-items-center justify-content-center m-0" onClick={showEditSamplingPoint} disabled={!selectedSamplingPoint || (!isReporterOrAdmin && shouldBeDateLocked(selectedSamplingPoint.samplingDate))}><PencilFill className="me-2"></PencilFill> {t("wtp.button.edit")}</Button>
                        <Button variant="outline-danger" className="d-flex align-items-center justify-content-center m-0" onClick={removeSamplingPoint} disabled={!selectedSamplingPoint}><Trash className="me-2"></Trash> {t("wtp.button.delete")}</Button>
                    </ButtonGroup>
                </Col>
            </Row>
            <Row className="mt-2">
                <Col>
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th></th>
                                <th className="align-middle">{t("wtp.measurements.sampling_results.date")}</th>
                                {getSamplingParametersToShow(inputDataReversed[selectedSamplingPointType]).map(x => <td key={x.name} className="text-center"><strong>{x.name}</strong><br />( {x.unit} )</td>)}
                            </tr>
                        </thead>
                        <tbody>
                            {dataToDisplay.length > 0 ?
                                dataToDisplay.map((x: WastewaterTreatementPlantSamplingPoint, i: number) => <tr key={i} onClick={() => setSelectedSamplingPoint(x)} className={selectedSamplingPoint === x ? "selected" : ""}>
                                    <td className="text-center">
                                        {!(!isReporterOrAdmin && shouldBeDateLocked(x.samplingDate)) && <Button variant="link" className="py-0" onClick={() => { setSelectedSamplingPoint(x); showEditSamplingPoint(); }}><Pencil /></Button>}
                                    </td>
                                    <td>{x.samplingDate && new Date(x.samplingDate).toLocaleDateString(i18n.language === "en" ? "en-US" : "el-GR", { year: "numeric", month: "2-digit", day: "2-digit" })}</td>
                                    {getSamplingParametersToShow(inputDataReversed[selectedSamplingPointType]).map(y => <td key={y.name} className="text-center">{x.samples.find(z => z.id === y.id)?.value ?? ""}</td>)}
                                </tr>)
                                : <tr>
                                    <td colSpan={2 + getSamplingParametersToShow(inputDataReversed[selectedSamplingPointType]).length} className="text-center">
                                        {t("wtp.table.no_data")}
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </Table>
                    {getPagination()}
                </Col>
            </Row>
            <hr></hr>
            <h4>{t("wtp.reuse.additional_elements.header")}</h4>
            <Form.Group as={Row} className="mb-2">
                <Form.Label column className="text-end" md={{ span: 5 }}>
                    {t("wtp.reuse.additional_elements.continuous_turbidity_check")}
                </Form.Label>
                <Col md={{ span: 2 }} className="pe-0">
                    <Form.Select
                        id="reuse.continuousTurbidityCheck"
                        name="reuse.continuousTurbidityCheck"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={(values as any).reuse?.continuousTurbidityCheck}
                        isInvalid={(touched as any).reuse?.continuousTurbidityCheck && !!(errors as any).reuse?.continuousTurbidityCheck}
                    >
                        <option key={0} value={"False"}>{t("no")}</option>
                        <option key={1} value={"True"}>{t("yes")}</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                        {(errors as any).reuse?.continuousTurbidityCheck}
                    </Form.Control.Feedback>
                </Col>
            </Form.Group>
            <hr></hr>
            <h4>{t("wtp.measurements.sampling_remarks.header")}</h4>
            <Form.Control
                as="textarea"
                rows={3}
                id="measurementsRemarks"
                name="measurementsRemarks"
                onBlur={handleChange}
                defaultValue={(values as any).measurementsRemarks ?? ""}
                isInvalid={(touched as any).measurementsRemarks && !!(errors as any).measurementsRemarks}
            />
            <Form.Control.Feedback type="invalid">
                {(errors as any).measurementsRemarks}
            </Form.Control.Feedback>
            {/* </Tab>
                <Tab eventKey="limits" title={t("wtp.main.limits")}>
                    <Row>
                        <Col>
                            <h4>{t("wtp.measurements.limits.header")}</h4>
                        </Col>
                        <Col>
                            <ButtonGroup className='float-end btn-group-sm mt-2' aria-label="Toolbar">
                                <Button variant="outline-primary" className="d-flex align-items-center justify-content-center m-0" onClick={() => setAddEnvironmentalPermitModalShow(true)}><PlusCircle className="me-2"></PlusCircle> {t("wtp.button.add_upper")}</Button>
                                <Button variant="outline-secondary" className="d-flex align-items-center justify-content-center m-0" onClick={() => setEditEnvironmentalPermitModalShow(true)} disabled={!selectedEnvironmentalPermit}><PencilFill className="me-2"></PencilFill> {t("wtp.button.edit")}</Button>
                                <Button variant="outline-danger" className="d-flex align-items-center justify-content-center m-0" onClick={removeEnvironmentalPermit} disabled={!selectedEnvironmentalPermit}><Trash className="me-2"></Trash> {t("wtp.button.delete")}</Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col>
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th className="align-middle text-center">{t("wtp.measurements.limits.date_issued")}</th>
                                        <th className="align-middle text-center">{t("wtp.measurements.limits.date_expired")}</th>
                                        {samplingParameters.map(x => <td key={x.name} style={{ minWidth: "100px" }} className="text-center"><strong>{x.name}</strong><br />( {x.unit} )</td>)}
                                        <th className="align-middle text-center">{t("wtp.measurements.limits.comments")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        (values as any).environmentalPermits.filter((x: WastewaterTreatementPlantEnvironmentalPermit) => x.samplingPointTypeId === selectedSamplingPointType).length === 0 ?
                                            <tr>
                                                <td colSpan={4 + samplingParameters.length} className="text-center">
                                                    {t("wtp.table.no_data")}
                                                </td>
                                            </tr> :
                                            (values as any).environmentalPermits.filter((x: WastewaterTreatementPlantEnvironmentalPermit) => x.samplingPointTypeId === selectedSamplingPointType).map((x: WastewaterTreatementPlantEnvironmentalPermit, i: number) => <tr key={i} onClick={() => setSelectedEnvironmentalPermit(x)} className={selectedEnvironmentalPermit === x ? "selected" : ""}>
                                                <td className="text-center align-middle"><Button variant="link" className="py-0" onClick={() => { setSelectedEnvironmentalPermit(x); setEditEnvironmentalPermitModalShow(true); }}><Pencil /></Button></td>
                                                <td className="align-middle text-center">{x.dateIssued && new Date(x.dateIssued).toLocaleDateString(i18n.language === "en" ? "en-US" : "el-GR", { year: "numeric", month: "2-digit", day: "2-digit" })}</td>
                                                <td className="align-middle text-center">{x.dateExpired && new Date(x.dateExpired).toLocaleDateString(i18n.language === "en" ? "en-US" : "el-GR", { year: "numeric", month: "2-digit", day: "2-digit" })}</td>
                                                {samplingParameters.map(y => <td key={y.name} className="text-center align-middle">{x.limits.find(z => z.id === y.id)?.limitValue ?? ""}</td>)}
                                                <td className="text-justify align-middle">{x.comments}</td>
                                            </tr>)
                                    }
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </Tab>
            </Tabs> */}
            <Modal
                show={addSamplingPointModalShow}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={() => setAddSamplingPointModalShow(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {t("wtp.measurements.sampling_results.modal.add.header")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Formik
                        initialValues={Object.assign(
                            {
                                samplingDate: "",
                                samplingPointTypeId: selectedSamplingPointType,
                            },
                            Object.fromEntries(samplingParameters.map((x, i) => [`parameter_${i}`, parametersInSamplingForm[i]?.parameterId ?? 0])),
                            Object.fromEntries(samplingParameters.map((x, i) => [`parameter_value_${i}`, parametersInSamplingForm[i]?.value ?? 0])),
                        )}
                        validationSchema={samplingSchema}
                        onSubmit={(v) => handleAddSamplingPointSubmit(v)}
                        innerRef={addSamplingPointFormikRef}
                    >
                        {props => <Form noValidate ref={addSamplingPointFormRef} onSubmit={props.handleSubmit}>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.sampling_results.date") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        type="date"
                                        id="samplingDate"
                                        name="samplingDate"
                                        value={props.values.samplingDate}
                                        onChange={props.handleChange}
                                        isInvalid={props.touched.samplingDate && !!props.errors.samplingDate}
                                        isValid={props.touched.samplingDate && !props.errors.samplingDate}
                                        max={new Date().toISOString().split("T")[0]}
                                        min={!isReporterOrAdmin ? (new Date() > new Date(`${new Date().getFullYear()}-06-01 00:00`) ? `${new Date().getFullYear()}-01-01` : `${new Date().getFullYear() - 1}-01-01`) : undefined}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.samplingDate}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.display_options.sampler_position") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Select
                                        id="samplingPointTypeId"
                                        name="samplingPointTypeId"
                                        value={props.values.samplingPointTypeId}
                                        onChange={v => props.setFieldValue("samplingPointTypeId", v.target.value)}
                                        isInvalid={props.touched.samplingPointTypeId && !!props.errors.samplingPointTypeId}
                                        isValid={props.touched.samplingPointTypeId && !props.errors.samplingPointTypeId}
                                    >
                                        <option value={-1} key="none">{t("wtp.measurements.sampling_results.select_sampler_location")}</option>
                                        {
                                            samplingPointTypes.map(x => <option value={x.id} key={x.id}>{i18n.language === "en" ? x.nameEn : x.name}</option>)
                                        }
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.samplingPointTypeId}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            {parametersInSamplingForm.map((y: any, i: number) => <Form.Group key={i} as={Row} className="mb-2">
                                <Col md={{ span: 4 }}>
                                    {/* <Form.Select
                                        id={`parameter_${i}`}
                                        name={`parameter_${i}`}
                                        value={y.parameterId}
                                        onChange={(e) => { props.setFieldValue(`parameter_${i}`, e.target.value); changeParameterInSamplingForm(y.id, e.target.value); }}
                                        isInvalid={props.touched[`parameter_${i}`] && !!props.errors[`parameter_${i}`]}
                                    >
                                        <option value={0} key="none">{t("wtp.measurements.sampling_results.select_parameter")}</option>
                                        {samplingParameters.map((x: any, i: number) => <option value={x.id} key={i}>{x.name}</option>)}
                                    </Form.Select> */}
                                    <Select
                                        noOptionsMessage={() => t("wtp.select.no_options")}
                                        options={[{ value: 0, label: t("wtp.measurements.sampling_results.select_parameter"), }].concat(samplingParameters.map((x: any, i: number) => { return { value: x.id, label: `${x.name} (${x.unit})`, } }))}
                                        id={`parameter_${i}`}
                                        name={`parameter_${i}`}
                                        onChange={v => { props.setFieldValue(`parameter_${i}`, v?.value); changeParameterInSamplingForm(y.id, v?.value.toString() || "0"); }}
                                        value={[{ value: 0, label: t("wtp.measurements.sampling_results.select_parameter"), }].concat(samplingParameters.map((x: any, i: number) => { return { value: x.id, label: `${x.name} (${x.unit})`, } })).find(x => x.value === props.values[`parameter_${i}`])}
                                        className={'react-select ' + (props.values[`parameter_${i}`] === 0 ? 'is-invalid' : '')}
                                        classNamePrefix={'react-select'}
                                        menuPosition={"fixed"}
                                        menuPlacement={"auto"}
                                        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors[`parameter_${i}`]}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col md={{ span: 7 }}>
                                    <Form.Control
                                        id={`parameter_value_${i}`}
                                        name={`parameter_value_${i}`}
                                        value={props.values[`parameter_value_${i}`]}
                                        onChange={props.handleChange}
                                        onBlur={(e) => { props.setFieldValue(`parameter_value_${i}`, e.target.value); changeParameterValueInSamplingForm(y.id, e.target.value); }}
                                        isInvalid={props.touched[`parameter_value_${i}`] && !!props.errors[`parameter_value_${i}`]}
                                        isValid={props.touched[`parameter_value_${i}`] && !props.errors[`parameter_value_${i}`]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors[`parameter_value_${i}`]}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col md={{ span: 1 }}>
                                    <Button active style={{ border: "none" }} variant="ghost" onClick={() => removeParameterInSamplingForm(y.id)}>
                                        <DashCircle color="red" />
                                    </Button>
                                </Col>
                            </Form.Group>)}
                            {parametersInSamplingForm.length < samplingParameters.length && <Form.Group as={Row} className="mb-2">
                                <Button onClick={addParameterInSamplingForm} style={{ width: "400px", margin: "0 auto", backgroundColor: "white", color: "gray", border: "2px dotted gray" }}>
                                    <Plus />&nbsp;{t("wtp.measurements.add_parameter")}
                                </Button>
                            </Form.Group>}
                        </Form>}
                    </Formik>
                    {sameParameterError && <Alert variant={'light'} className="m-0">
                        <div className="text-danger">{t("wtp.measurements.sampling_results.modal.same_parameter")}</div>
                    </Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="info" className="me-auto" onClick={() => { setAddSamplingPointModalShow(false); setAddSamplingPointFileModalShow(true); }}>{t("wtp.button.add_multiple")}</Button>
                    <Button disabled={parametersInSamplingForm.length === 0} onClick={() => addSamplingPointFormRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }))}>{t("wtp.button.add")}</Button>
                    <Button variant="secondary" onClick={() => setAddSamplingPointModalShow(false)}>{t("wtp.button.cancel")}</Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={editSamplingPointModalShow}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={() => setEditSamplingPointModalShow(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {t("wtp.measurements.sampling_results.modal.edit.header")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Formik
                        initialValues={Object.assign(
                            {
                                samplingDate: selectedSamplingPoint?.samplingDate ? new Date(selectedSamplingPoint?.samplingDate.replace("+03:00", "+00:00").replace("+02:00", "+00:00")).toISOString().substring(0, 10) : "",
                                samplingPointTypeId: selectedSamplingPoint?.samplingPointTypeId.toString() || -1,
                            },
                            Object.fromEntries(samplingParameters.map((x, i) => [`parameter_${i}`, parametersInEditSamplingForm[i]?.parameterId ?? 0])),
                            Object.fromEntries(samplingParameters.map((x, i) => [`parameter_value_${i}`, parametersInEditSamplingForm[i]?.value ?? 0])),
                        )}
                        validationSchema={samplingSchema}
                        onSubmit={(v) => handleEditSamplingPointSubmit(v)}
                        innerRef={editSamplingPointFormikRef}
                    >
                        {props => <Form noValidate ref={editSamplingPointFormRef} onSubmit={props.handleSubmit}>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.sampling_results.date") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        type="date"
                                        id="samplingDate"
                                        name="samplingDate"
                                        value={props.values.samplingDate}
                                        onChange={props.handleChange}
                                        isInvalid={props.touched.samplingDate && !!props.errors.samplingDate}
                                        isValid={props.touched.samplingDate && !props.errors.samplingDate}
                                        disabled
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.samplingDate}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.display_options.sampler_position") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Select
                                        id="samplingPointTypeId"
                                        name="samplingPointTypeId"
                                        value={props.values.samplingPointTypeId}
                                        onChange={v => props.setFieldValue("samplingPointTypeId", v.target.value)}
                                        isInvalid={props.touched.samplingPointTypeId && !!props.errors.samplingPointTypeId}
                                        isValid={props.touched.samplingPointTypeId && !props.errors.samplingPointTypeId}
                                        disabled
                                    >
                                        <option value={-1} key="none">{t("wtp.measurements.sampling_results.select_sampler_location")}</option>
                                        {
                                            samplingPointTypes.map(x => <option value={x.id} key={x.id}>{i18n.language === "en" ? x.nameEn : x.name}</option>)
                                        }
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.samplingPointTypeId}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            {parametersInEditSamplingForm.map((y: any, i: number) => <Form.Group key={i} as={Row} className="mb-2">
                                <Col md={{ span: 4 }}>
                                    {/* <Form.Select
                                        id={`parameter_${i}`}
                                        name={`parameter_${i}`}
                                        value={y.parameterId}
                                        onChange={(e) => { props.setFieldValue(`parameter_${i}`, e.target.value); changeParameterInSamplingForm(y.id, e.target.value); }}
                                        isInvalid={props.touched[`parameter_${i}`] && !!props.errors[`parameter_${i}`]}
                                    >
                                        <option value={0} key="none">{t("wtp.measurements.sampling_results.select_parameter")}</option>
                                        {samplingParameters.map((x: any, i: number) => <option value={x.id} key={i}>{x.name}</option>)}
                                    </Form.Select> */}
                                    <Select
                                        noOptionsMessage={() => t("wtp.select.no_options")}
                                        options={[{ value: 0, label: t("wtp.measurements.sampling_results.select_parameter"), }].concat(samplingParameters.map((x: any, i: number) => { return { value: x.id, label: `${x.name} (${x.unit})`, } }))}
                                        id={`parameter_${i}`}
                                        name={`parameter_${i}`}
                                        onChange={v => { props.setFieldValue(`parameter_${i}`, v?.value); changeParameterInEditSamplingForm(y.id, v?.value.toString() || "0"); }}
                                        value={[{ value: 0, label: t("wtp.measurements.sampling_results.select_parameter"), }].concat(samplingParameters.map((x: any, i: number) => { return { value: x.id, label: `${x.name} (${x.unit})`, } })).find(x => x.value === props.values[`parameter_${i}`])}
                                        className={'react-select ' + (props.values[`parameter_${i}`] === 0 ? 'is-invalid' : '')}
                                        classNamePrefix={'react-select'}
                                        menuPosition={"fixed"}
                                        menuPlacement={"auto"}
                                        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors[`parameter_${i}`]}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col md={{ span: 7 }}>
                                    <Form.Control
                                        id={`parameter_value_${i}`}
                                        name={`parameter_value_${i}`}
                                        value={props.values[`parameter_value_${i}`]}
                                        onChange={props.handleChange}
                                        onBlur={(e) => { props.setFieldValue(`parameter_value_${i}`, e.target.value); changeParameterValueInEditSamplingForm(y.id, e.target.value); }}
                                        isInvalid={props.touched[`parameter_value_${i}`] && !!props.errors[`parameter_value_${i}`]}
                                        isValid={props.touched[`parameter_value_${i}`] && !props.errors[`parameter_value_${i}`]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors[`parameter_value_${i}`]}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col md={{ span: 1 }}>
                                    <Button active style={{ border: "none" }} variant="ghost" onClick={() => removeParameterInEditSamplingForm(y.id)}>
                                        <DashCircle color="red" />
                                    </Button>
                                </Col>
                            </Form.Group>)}
                            {parametersInEditSamplingForm.length < samplingParameters.length && <Form.Group as={Row} className="mb-2">
                                <Button onClick={addParameterInEditSamplingForm} style={{ width: "400px", margin: "0 auto", backgroundColor: "white", color: "gray", border: "2px dotted gray" }}>
                                    <Plus />&nbsp;{t("wtp.measurements.add_parameter")}
                                </Button>
                            </Form.Group>}
                        </Form>}
                    </Formik>
                    {sameParameterError && <Alert variant={'light'} className="m-0">
                        <div className="text-danger">{t("wtp.measurements.sampling_results.modal.same_parameter")}</div>
                    </Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => editSamplingPointFormRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }))}>{t("wtp.button.update")}</Button>
                    <Button variant="secondary" onClick={() => setEditSamplingPointModalShow(false)}>{t("wtp.button.cancel")}</Button>
                </Modal.Footer>
            </Modal>
            {/* <Modal
                show={editSamplingPointModalShow}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={() => setEditSamplingPointModalShow(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {t("wtp.measurements.sampling_results.modal.edit.header")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Formik
                        initialValues={Object.assign(
                            {
                                samplingDate: selectedSamplingPoint?.samplingDate ? new Date(selectedSamplingPoint?.samplingDate.replace("+03:00", "+00:00").replace("+02:00", "+00:00")).toISOString().substring(0, 10) : "",
                                samplingPointTypeId: selectedSamplingPoint?.samplingPointTypeId.toString() || -1,
                            },
                            Object.fromEntries(samplingParameters.map(x => [x.name, selectedSamplingPoint?.samples.find(y => y.id === x.id)?.value ?? ""]))
                        )}
                        validationSchema={samplingSchema}
                        onSubmit={(v) => handleEditSamplingPointSubmit(v)}
                    >
                        {props => <Form noValidate ref={editSamplingPointFormRef} onSubmit={props.handleSubmit}>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.sampling_results.date") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        type="date"
                                        id="samplingDate"
                                        name="samplingDate"
                                        value={props.values.samplingDate}
                                        onChange={props.handleChange}
                                        isInvalid={props.touched.samplingDate && !!props.errors.samplingDate}
                                        isValid={props.touched.samplingDate && !props.errors.samplingDate}
                                        disabled
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.samplingDate}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.display_options.sampler_position") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Select
                                        id="samplingPointTypeId"
                                        name="samplingPointTypeId"
                                        value={props.values.samplingPointTypeId}
                                        onChange={v => props.setFieldValue("samplingPointTypeId", v.target.value)}
                                        isInvalid={props.touched.samplingPointTypeId && !!props.errors.samplingPointTypeId}
                                        isValid={props.touched.samplingPointTypeId && !props.errors.samplingPointTypeId}
                                        disabled
                                    >
                                        <option value={-1} key="none">{t("wtp.measurements.sampling_results.select_sampler_location")}</option>
                                        {
                                            samplingPointTypes.map(x => <option value={x.id} key={x.id}>{i18n.language === "en" ? x.nameEn : x.name}</option>)
                                        }
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.samplingPointTypeId}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            {selectedSamplingPoint?.samples.map(x => <Form.Group key={x.name} as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{`${x.name} (${samplingParameters.find(y => y.name === x.name)?.unit})`}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        placeholder={x.name!}
                                        id={x.name!}
                                        name={x.name!}
                                        value={props.values[x.name!]}
                                        onChange={props.handleChange}
                                        isInvalid={props.touched[x.name!] && !!props.errors[x.name!]}
                                        isValid={props.touched[x.name!] && !props.errors[x.name!]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors[x.name!]}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>)}
                        </Form>}
                    </Formik>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => editSamplingPointFormRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }))}>{t("wtp.button.update")}</Button>
                    <Button variant="secondary" onClick={() => setEditSamplingPointModalShow(false)}>{t("wtp.button.cancel")}</Button>
                </Modal.Footer>
            </Modal> */}
            <MeasurementsFromFile isReuse={true} samplingParameters={samplingParameters} samplingPointTypes={samplingPointTypes} samplingPointType={selectedSamplingPointType} show={addSamplingPointFileModalShow} setShow={setAddSamplingPointFileModalShow} />
            <Modal
                show={addEnvironmentalPermitModalShow}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={() => setAddEnvironmentalPermitModalShow(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {t("wtp.measurements.limits.modal.add.header")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Formik
                        initialValues={Object.assign(
                            {
                                dateIssued: "",
                                dateExpired: "",
                                samplingPointTypeId: selectedSamplingPointType,
                                comments: "",
                            },
                            Object.fromEntries(samplingParameters.map((x, i) => [`parameter_${i}`, parametersInLimitsForm[i]?.parameterId ?? 0])),
                            Object.fromEntries(samplingParameters.map((x, i) => [`parameter_value_${i}`, parametersInLimitsForm[i]?.limitValue ?? 0])),
                        )}
                        validationSchema={limitsSchema}
                        onSubmit={(v) => handleAddEnvironmentalPermitSubmit(v)}
                        innerRef={addEnvironmentalPermitFormikRef}
                    >
                        {props => <Form noValidate ref={addEnvironmentalPermitFormRef} onSubmit={props.handleSubmit}>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.limits.date_issued") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        type="date"
                                        id="dateIssued"
                                        name="dateIssued"
                                        value={props.values.dateIssued}
                                        onChange={props.handleChange}
                                        isInvalid={props.touched.dateIssued && !!props.errors.dateIssued}
                                        isValid={props.touched.dateIssued && !props.errors.dateIssued}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.dateIssued}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.limits.date_expired") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        type="date"
                                        id="dateExpired"
                                        name="dateExpired"
                                        value={props.values.dateExpired}
                                        onChange={props.handleChange}
                                        isInvalid={props.touched.dateExpired && !!props.errors.dateExpired}
                                        isValid={props.touched.dateExpired && !props.errors.dateExpired}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.dateExpired}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.display_options.sampler_position") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Select
                                        id="samplingPointTypeId"
                                        name="samplingPointTypeId"
                                        value={props.values.samplingPointTypeId}
                                        onChange={v => props.setFieldValue("samplingPointTypeId", v.target.value)}
                                        isInvalid={props.touched.samplingPointTypeId && !!props.errors.samplingPointTypeId}
                                        isValid={props.touched.samplingPointTypeId && !props.errors.samplingPointTypeId}
                                    >
                                        <option value={-1} key="none">{t("wtp.measurements.sampling_results.select_sampler_location")}</option>
                                        {
                                            samplingPointTypes.map(x => <option value={x.id} key={x.id}>{i18n.language === "en" ? x.nameEn : x.name}</option>)
                                        }
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.samplingPointTypeId}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.limits.comments")}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        id="comments"
                                        name="comments"
                                        value={props.values.comments}
                                        onChange={v => props.setFieldValue("comments", v.target.value)}
                                        isInvalid={props.touched.comments && !!props.errors.comments}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.comments}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            {parametersInLimitsForm.map((y: any, i: number) => <Form.Group key={i} as={Row} className="mb-2">
                                <Col md={{ span: 4 }}>
                                    {/* <Form.Select
                                        id={`parameter_${i}`}
                                        name={`parameter_${i}`}
                                        value={y.parameterId}
                                        onChange={(e) => { props.setFieldValue(`parameter_${i}`, e.target.value); changeParameterInLimitsForm(y.id, e.target.value); }}
                                        isInvalid={props.touched[`parameter_${i}`] && !!props.errors[`parameter_${i}`]}
                                    >
                                        <option value={0} key="none">{t("wtp.measurements.sampling_results.select_parameter")}</option>
                                        {samplingParameters.map((x: any, i: number) => <option value={x.id} key={i}>{x.name}</option>)}
                                    </Form.Select> */}
                                    <Select
                                        noOptionsMessage={() => t("wtp.select.no_options")}
                                        options={[{ value: 0, label: t("wtp.measurements.sampling_results.select_parameter"), }].concat(samplingParameters.map((x: any, i: number) => { return { value: x.id, label: `${x.name} (${x.unit})`, } }))}
                                        id={`parameter_${i}`}
                                        name={`parameter_${i}`}
                                        onChange={v => { props.setFieldValue(`parameter_${i}`, v?.value); changeParameterInLimitsForm(y.id, v?.value.toString() || "0"); }}
                                        value={[{ value: 0, label: t("wtp.measurements.sampling_results.select_parameter"), }].concat(samplingParameters.map((x: any, i: number) => { return { value: x.id, label: `${x.name} (${x.unit})`, } })).find(x => x.value === props.values[`parameter_${i}`])}
                                        className={'react-select ' + (props.values[`parameter_${i}`] === 0 ? 'is-invalid' : '')}
                                        classNamePrefix={'react-select'}
                                        menuPosition={"fixed"}
                                        menuPlacement={"auto"}
                                        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors[`parameter_${i}`]}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col md={{ span: 7 }}>
                                    <Form.Control
                                        id={`parameter_value_${i}`}
                                        name={`parameter_value_${i}`}
                                        value={y.limitValue}
                                        onChange={(e) => { props.setFieldValue(`parameter_value_${i}`, e.target.value); changeParameterValueInLimitsForm(y.id, e.target.value); }}
                                        isInvalid={props.touched[`parameter_value_${i}`] && !!props.errors[`parameter_value_${i}`]}
                                        isValid={props.touched[`parameter_value_${i}`] && !props.errors[`parameter_value_${i}`]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors[`parameter_value_${i}`]}
                                    </Form.Control.Feedback>
                                </Col>
                                <Col md={{ span: 1 }}>
                                    <Button active style={{ border: "none" }} variant="ghost" onClick={() => removeParameterInLimitsForm(y.id)}>
                                        <DashCircle color="red" />
                                    </Button>
                                </Col>
                            </Form.Group>)}
                            {parametersInLimitsForm.length < samplingParameters.length && <Form.Group as={Row} className="mb-2">
                                <Button onClick={addParameterInLimitsForm} style={{ width: "400px", margin: "0 auto", backgroundColor: "white", color: "gray", border: "2px dotted gray" }}>
                                    <Plus />&nbsp;{t("wtp.measurements.add_parameter")}
                                </Button>
                            </Form.Group>}
                        </Form>}
                    </Formik>
                    {sameParameterError && <Alert variant={'light'} className="m-0">
                        <div className="text-danger">{t("wtp.measurements.sampling_results.modal.same_parameter")}</div>
                    </Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => addEnvironmentalPermitFormRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }))}>{t("wtp.button.add")}</Button>
                    <Button variant="secondary" onClick={() => setAddEnvironmentalPermitModalShow(false)}>{t("wtp.button.cancel")}</Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={editEnvironmentalPermitModalShow}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={() => setEditEnvironmentalPermitModalShow(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {t("wtp.measurements.limits.modal.edit.header")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Formik
                        initialValues={Object.assign(
                            {
                                dateIssued: selectedEnvironmentalPermit?.dateIssued ? new Date(selectedEnvironmentalPermit?.dateIssued.replace("+02:00", "+00:00")).toISOString().substring(0, 10) : "",
                                dateExpired: selectedEnvironmentalPermit?.dateExpired ? new Date(selectedEnvironmentalPermit?.dateExpired.replace("+02:00", "+00:00")).toISOString().substring(0, 10) : "",
                                samplingPointTypeId: selectedEnvironmentalPermit?.samplingPointTypeId.toString() || -1,
                                comments: selectedEnvironmentalPermit?.comments ?? "",
                            },
                            Object.fromEntries(samplingParameters.map(x => [x.name, selectedEnvironmentalPermit?.limits.find(y => y.id === x.id)?.limitValue ?? ""]))
                        )}
                        validationSchema={limitsSchema}
                        onSubmit={(v) => handleEditEnvironmentalPermitSubmit(v)}
                    >
                        {props => <Form noValidate ref={editEnvironmentalPermitFormRef} onSubmit={props.handleSubmit}>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.limits.date_issued") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        type="date"
                                        id="dateIssued"
                                        name="dateIssued"
                                        value={props.values.dateIssued}
                                        onChange={props.handleChange}
                                        isInvalid={props.touched.dateIssued && !!props.errors.dateIssued}
                                        isValid={props.touched.dateIssued && !props.errors.dateIssued}
                                        disabled
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.dateIssued}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.limits.date_expired") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        type="date"
                                        id="dateExpired"
                                        name="dateExpired"
                                        value={props.values.dateExpired}
                                        onChange={props.handleChange}
                                        isInvalid={props.touched.dateExpired && !!props.errors.dateExpired}
                                        isValid={props.touched.dateExpired && !props.errors.dateExpired}
                                        disabled
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.dateExpired}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.display_options.sampler_position") + " *"}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Select
                                        id="samplingPointTypeId"
                                        name="samplingPointTypeId"
                                        value={props.values.samplingPointTypeId}
                                        onChange={v => props.setFieldValue("samplingPointTypeId", v.target.value)}
                                        isInvalid={props.touched.samplingPointTypeId && !!props.errors.samplingPointTypeId}
                                        isValid={props.touched.samplingPointTypeId && !props.errors.samplingPointTypeId}
                                        disabled
                                    >
                                        <option value={-1} key="none">{t("wtp.measurements.sampling_results.select_sampler_location")}</option>
                                        {
                                            samplingPointTypes.map(x => <option value={x.id} key={x.id}>{i18n.language === "en" ? x.nameEn : x.name}</option>)
                                        }
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.samplingPointTypeId}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{t("wtp.measurements.limits.comments")}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        id="comments"
                                        name="comments"
                                        value={props.values.comments}
                                        onChange={v => props.setFieldValue("comments", v.target.value)}
                                        isInvalid={props.touched.comments && !!props.errors.comments}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors.comments}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            {selectedEnvironmentalPermit?.limits.map(x => <Form.Group key={x.name} as={Row} className="mb-2">
                                <Form.Label column className="text-end" md={{ span: 4 }}>{`${x.name} (${samplingParameters.find(y => y.name === x.name)?.unit})`}</Form.Label>
                                <Col md={{ span: 8 }}>
                                    <Form.Control
                                        placeholder={x.name!}
                                        id={x.name!}
                                        name={x.name!}
                                        value={props.values[x.name!]}
                                        onChange={props.handleChange}
                                        isInvalid={props.touched[x.name!] && !!props.errors[x.name!]}
                                        isValid={props.touched[x.name!] && !props.errors[x.name!]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {props.errors[x.name!]}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>)}
                        </Form>}
                    </Formik>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => editEnvironmentalPermitFormRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }))}>{t("wtp.button.update")}</Button>
                    <Button variant="secondary" onClick={() => setEditEnvironmentalPermitModalShow(false)}>{t("wtp.button.cancel")}</Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={checkComplianceModalShow}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                onHide={() => setCheckComplianceModalShow(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {t("wtp.measurements.sampling_results.check_reuse")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {/* <ul><li><h5>{t("wtp.measurements.compliance_reuse_kya")}</h5></li></ul>
                    {complianceReuseKyaResults.length > 0 ? <Table striped bordered hover size="sm" className="mt-2">
                        <thead>
                            <tr>
                                <th className="text-center">{t("query.wtp.year")}</th>
                                {complianceReuseKyaResults.flatMap(x => x.results).map(x => x?.parameterId).filter((item, i, ar) => ar.indexOf(item) === i).map((x, i) => <th key={i} className="text-center">{samplingParameters.find(p => p.id === x)?.name}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                complianceReuseKyaResults.sort((a, b) => (b.year ?? 0) - (a.year ?? 0)).map((x: ReuseCheckResult, i: number) => <tr key={i}>
                                    <td className="text-center">{x.year}</td>
                                    {complianceReuseKyaResults.flatMap(x => x.results).map(x => x?.parameterId).filter((item, i, ar) => ar.indexOf(item) === i).map((y, i) => <td className="text-center">{getComplianceStatus(y ?? 0, x.results ?? [])}</td>)}
                                </tr>)
                            }
                        </tbody>
                    </Table> : t("wtp.table.no_data")} */}
                    <ul><li><h5>{t("wtp.measurements.compliance_reuse_eu")}</h5></li></ul>
                    {complianceReuseEuResults.length > 0 ? generateComplianceTable(complianceReuseEuResults) : t("wtp.table.no_data")}
                </Modal.Body>
                {context.state.userInfo?.roles.findIndex(x => x.toLowerCase() === "reporter") !== -1 && <Modal.Footer>
                    {/* <Button onClick={() => recalculateReuseKya()} disabled={loading}>{t("wtp.button.recalculate_reuse_kya")}</Button> */}
                    <Button onClick={() => recalculateReuseEu()} disabled={loading}>{t("wtp.button.recalculate_reuse_eu")}</Button>
                    <Button variant="secondary" onClick={() => setCheckComplianceModalShow(false)}>{t("wtp.button.close")}</Button>
                </Modal.Footer>}
            </Modal>
            <SamplerPositionsModal
                wastewaterTreatmentPlantId={(values as any).id}
                samplingPointTypes={samplingPointTypes}
                show={samplingModalShow}
                setShow={setSamplingModalShow}
                setSamplingPointTypes={setSamplingPointTypes}
            />
        </>
    )
}

export default WtpManageMeasurementsReuse;
